<script setup lang="tsx">
import type { AppliedFilters } from '@/components/distiller/types';
import type { CustomCells, HybridlyRow } from '@/components/table/Table';

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';
import { useAuth } from '@/hooks/useAuth';
import { useFilters } from '@/hooks/useFilters';
import { useHybridlyTable } from '@/hooks/useHybridlyTable';
import { useTableRowClick } from '@/hooks/useTableRowClick';

import BulkRestoreButton from '@/components/table/BulkRestoreButton.vue';
import BulkTrashButton from '@/components/table/BulkTrashButton.vue';
import Button from '@/components/button/Button.vue';
import EmptyState from '@/components/emptyState/EmptyState.vue';
import IndexPageLayout from '@/components/pageLayout/IndexPageLayout.vue';
import TableBadgesCell from '@/components/table/TableBadgesCell.vue';
import TableBooleanCell from '@/components/table/TableBooleanCell.vue';
import TableDateTimeCell from '@/components/table/TableDateTimeCell.vue';
import TableEnumCell from '@/components/table/TableEnumCell.vue';
import TableTruncatedTextCell from '@/components/table/TableTruncatedTextCell.vue';
import TabNavDivider from '@/components/tabNav/TabNavDivider.vue';
import TabNavItem from '@/components/tabNav/TabNavItem.vue';

import Building from '@/icons/line/building.svg';
import UserCircle from '@/icons/line/user-circle.svg';
import Plus from '@/icons/line/plus.svg';

type SuccessStoryRow = HybridlyRow<App.SuccessStories.Data.SuccessStoryData>;

const props = defineProps<{
  successStories: Table<App.SuccessStories.Data.SuccessStoryData>;
  appliedFilters: AppliedFilters;
  distiller: App.Search.Data.DistillerData;
  trashActive: boolean;
  counts: {
    active_count: number;
    trashed_count: number;
  };
}>();

const { hasPermission } = useAuth();
const { table, tableSelectionState } = useHybridlyTable<App.SuccessStories.Data.SuccessStoryData>(
  props,
  'successStories'
);
const { handleRowClick } = useTableRowClick();
const { encodeFilters } = useFilters();

const encodedFilters = computed(() => encodeFilters(props.appliedFilters));

const customCells = {
  title: ({ row, column }) => <TableTruncatedTextCell text={row.value(column.key)} />,
  subject: ({ row, column }) => <TableEnumCell enumData={row.value(column.key)} />,
  user: ({ row, column }) => (
    <TableBadgesCell
      badgesToDisplay={1}
      itemIcon={UserCircle}
      items={[row.value(column.key).name]}
    />
  ),
  school: ({ row, column }) => (
    <TableBadgesCell badgesToDisplay={1} itemIcon={Building} items={[row.value(column.key).name]} />
  ),
  student: ({ row, column }) => (
    <TableBadgesCell
      badgesToDisplay={1}
      items={row.value(column.key)?.name ? [row.value(column.key).name] : []}
    />
  ),
  is_parent: ({ row, column }) => <TableBooleanCell boolean={row.value(column.key) ?? false} />,
  created_at: ({ row, column }) => <TableDateTimeCell date={row.value(column.key)} />
} as CustomCells<App.SuccessStories.Data.SuccessStoryData>;

function handleSuccessStoryRowClick(event: MouseEvent, row: SuccessStoryRow) {
  const url = route('success-stories.show', {
    successStory: row.key
  });
  handleRowClick(url, event);
}
</script>


					<script lang="tsx">
					import __hybridly_layout_0 from '~/resources/layouts/default.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <IndexPageLayout
    title="Success Stories"
    :table="table"
    :appliedFilters="appliedFilters"
    :distiller="distiller"
    :customCells="customCells"
    :stickyEndColumns="1"
    :stickyStartColumns="1"
    :clickableRows="!trashActive"
    @clickRow="($event, row) => handleSuccessStoryRowClick($event, row as SuccessStoryRow)"
    :tableSelectionState="tableSelectionState"
  >
    <template #rightButtons>
      <template v-if="tableSelectionState.hasSelection">
        <BulkTrashButton
          v-if="!trashActive"
          recordType="Success Stories"
          :route="route('success-stories.bulk-delete')"
          :tableSelectionState="tableSelectionState"
        />

        <BulkRestoreButton
          v-if="trashActive"
          recordType="Success Stories"
          :route="route('success-stories.bulk-restore')"
          :tableSelectionState="tableSelectionState"
        />
      </template>
      <template v-else>
        <Button
          v-if="hasPermission('create-success-story')"
          :color="ButtonColor.primary"
          :href="route('success-stories.create')"
          :iconLeft="Plus"
          :size="ButtonSize.sm"
          :variant="ButtonVariant.solid"
        >
          Add a new success story
        </Button>
      </template>
    </template>

    <template #tabs>
      <TabNavItem
        :badgeLabel="counts.active_count"
        :isActive="!trashActive"
        label="Active"
        variant="inline"
        :href="
          route('success-stories.index', {
            ...(encodedFilters ? { filters: encodedFilters } : {})
          })
        "
      />
      <TabNavDivider layout="inline" />
      <TabNavItem
        :badgeLabel="counts.trashed_count ?? 0"
        :isActive="trashActive"
        label="Trashed"
        variant="inline"
        :href="
          route('success-stories.index', {
            trashed: true,
            ...(encodedFilters ? { filters: encodedFilters } : {})
          })
        "
      />
    </template>

    <template #emptyState>
      <EmptyState
        v-if="trashActive"
        title="No success stories were moved to the trash"
        description="Trashed success stories will appear on this screen"
      />
      <EmptyState
        v-else
        title="No success stories found"
        description="Create a new success story by clicking the button below"
        actionLabel="Add a success story"
        :actionHref="route('success-stories.create')"
      />
    </template>
  </IndexPageLayout>
</template>
